import type { FunctionComponent } from 'preact'
import { useState, useRef, useEffect } from 'preact/hooks'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  approachingTurnLimit?: boolean
  remainingTurns?: number
}

export const ChatInput: FunctionComponent<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Ask me anything...',
  approachingTurnLimit = false,
  remainingTurns = 0,
}) => {
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-focus the input when it becomes enabled (after AI response)
  useEffect(() => {
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [disabled])

  const handleSubmit = (e: Event) => {
    e.preventDefault()
    if (message.trim() && !disabled) {
      onSend(message.trim())
      setMessage('')
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    // Submit on Enter (but allow Shift+Enter for new lines)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleInput = (e: Event) => {
    const target = e.target as HTMLTextAreaElement
    setMessage(target.value)
    
    // Auto-resize textarea
    target.style.height = 'auto'
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`
  }

  return (
    <div className="border-t bg-white dark:bg-slate-900 dark:border-slate-700 p-4">
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className="w-full px-4 py-2.5 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg 
                     bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     resize-none disabled:bg-gray-50 dark:disabled:bg-slate-700 disabled:text-gray-500 dark:disabled:text-gray-400
                     text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500"
            style={{ minHeight: '42px', maxHeight: '120px' }}
          />
          
          {/* Character count (optional) */}
          {message.length > 0 && (
            <div className="absolute bottom-2 right-2 text-xs text-gray-400 dark:text-gray-500">
              {message.length}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={disabled || !message.trim()}
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 
                   text-white rounded-lg transition-colors flex items-center gap-2
                   disabled:cursor-not-allowed"
          aria-label="Send message"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </form>

      {/* Hint text / remaining turns */}
      <p className={`mt-2 text-xs text-center ${
        approachingTurnLimit ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-gray-500'
      }`}>
        {approachingTurnLimit
          ? `${remainingTurns} message${remainingTurns !== 1 ? 's' : ''} remaining in this session`
          : 'Press Enter to send • Shift+Enter for new line'
        }
      </p>
    </div>
  )
}
